/* FileMultiEditViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./FileMultiEditViewRenderer");

class FileMultiEditViewComponent extends EventEmitter {
    constructor(logger, gameEditor, userSession, MUF, solutionsManager) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = MUF;
        this._tabComponent = this._gameEditor.getTabCmpnt();

        this._userSession = userSession;

        this._solutionDefinition = solutionsManager.getActiveSolution();
        this._solutionName = this._solutionDefinition.name || "";
        this._osImages = undefined;

        this._gameEditor.hideCabinetCpmnt();
        this._renderer = new Renderer(this);

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._tabComponent.on("DirtyChanged", (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this)));
    }

    _unregisterListeners() {
        this._tabComponent.on("DirtyChanged", (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this)));
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._osImages = undefined;
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn(err, "OsImageDetailsViewComponent.shutdown() error.");
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged(dirtyState) {
        this.emit("Dirty", dirtyState);

        let onBeforeUnloadFunc = () => {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive"
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text"
                    }
                })
                .then(() => {
                    return this.saveOsImagesData().then(_.noop); // it will not close the tab if it will have something in result
                })
                .catch((error) => {
                    if (error.userRejected) {
                        return this._modalUserFeedback.hideDisplayed().then(_.noop);
                    } else {
                        this._logger.warn({ error }, "Error in onBeforeUnloadFunc! Err:");
                    }
                });
        };

        if (this.isDirty()) {
            this._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
        } else {
            this._gameEditor.clearOnBeforeUnload();
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/
    get editorMode() {
        return this.getEditorMode();
    }

    getEditorMode() {
        return this._gameEditor.getInstanceEditorStateProperty("details.editorMode") || "general";
    }

    set editorMode(editorState) {
        return this.setEditorMode(editorState);
    }

    setEditorMode(editorState, silent) {
        let old = this.getEditorMode();
        this._gameEditor.setInstanceEditorStateProperty("details.editorMode", editorState);
        if (!silent) {
            this.emit("EditorModeChanged", editorState, old);
        }
        return this;
    }

    isDirty() {
        return this._tabComponent.getDirty();
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    get osImages() {
        return this._osImages;
    }

    getOsImageJType() {
        return this._gameEditor.getOsImageJType();
    }

    get osImagesPristineProperties() {
        return this._osImages.map((osImage) => this._gameEditor.getOsImagePristineValues(osImage));
    }

    setTabDirty(state) {
        this._tabComponent.setDirty(state);
    }

    closeTab() {
        return this._gameEditor.closeEditor();
    }

    whenModelIsReady() {
        return this._gameEditor
            .whenEditorIsReady()
            .then(() => {
                this._osImages = this._gameEditor.getOsImages();
            })
            .catch((err) => {
                this._logger.warn(err, "FileMultiEditViewComponent.whenModelIsReady() error");
                this._modalUserFeedback.displayError({
                    errorMsg: "This files cannot be opened.",
                    errorCode: ""
                });
                return Promise.reject(err);
            });
    }

    saveOsImagesData(dataToSave) {
        dataToSave = dataToSave || this.renderer.getFormData();

        this._modalUserFeedback.displayLoading({
            message: "Updating files... This could take a moment"
        });
        const showProgFunc = (prog) => {
            this._modalUserFeedback.showLoadingProgress(prog.progressCount + " of " + prog.totalCount + " file" + (prog.totalCount > 1 ? "s" : ""));
        };

        const tenantId = this._gameEditor.activeOrgId;
        const getOperation = (updateProps) => (osImage) => ({
            actionType: "entity",
            action: "update",
            entityType: this._gameEditor.osImageEntity.getEntityType(),
            entityId: osImage.id,
            properties: updateProps
        });

        const operations = this._osImages.map(getOperation(dataToSave));
        const bulkUpdate = this._gameEditor.osImageEntity.doBulk(tenantId, operations, showProgFunc);

        return bulkUpdate
            .then((/*result*/) => {
                this.setTabDirty(false);
            })
            .catch((err) => {
                this._logger.warn({ error: err }, "FileDetailsViewComponent.saveOsImagesData() error.");

                if (_.get(err, "errorCode") === "EntityInteractionsError") {
                    this.setTabDirty(false);
                }
                return err && Promise.reject(err);
            })
            .then(() => this._gameEditor.loadOsImages())
            .then((osImages) => {
                this._osImages = osImages;
                if (!osImages.length) {
                    const error = {
                        errorMsg:
                            "The selected files have all been deleted by another user before changes could be saved. This form is now going to be closed.",
                        errorCode: "EntityInteractionsError",
                        title: "Error"
                    };
                    return Promise.reject(error);
                }
            });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = FileMultiEditViewComponent;

FileMultiEditViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.solutionsManager"
];

FileMultiEditViewComponent.$displayDetails = {
    type: "View",
    name: "File Multi-Edit",
    icon: "common.edit",
    shown: false,
    title: "MultiEdit",
    order: 1
};
